/**
 * VAGUE 30A — Proactive alerts banner for the telepro portal.
 *
 * Polls GET /api/telepro/me/alerts every 60 s and renders each alert as a
 * dismissible strip. Alerts are stateless server-side; dismissals are kept
 * local (sessionStorage) so refreshing the tab doesn't hide today's insight.
 */
import React, { useEffect, useState, useCallback } from "react";
import { teleproApi } from "../../lib/teleproApi";
import { X, Warning, Sparkle, Flag, ArrowRight } from "@phosphor-icons/react";

const DISMISS_KEY_PREFIX = "mc68.telepro.alerts.dismissed.";

// Rehydrate today's dismissals so alerts don't come back after a page reload.
function isDismissedToday(alertId) {
  try {
    const key = DISMISS_KEY_PREFIX + new Date().toISOString().slice(0, 10);
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    return raw.split(",").includes(alertId);
  } catch (_e) {
    return false;
  }
}

function markDismissedToday(alertId) {
  try {
    const key = DISMISS_KEY_PREFIX + new Date().toISOString().slice(0, 10);
    const raw = sessionStorage.getItem(key) || "";
    const list = new Set(raw.split(",").filter(Boolean));
    list.add(alertId);
    sessionStorage.setItem(key, Array.from(list).join(","));
  } catch (_e) { /* noop */ }
}

const LEVEL_STYLES = {
  info:      { bg: "bg-blue-50 border-blue-200 text-blue-900", icon: <Sparkle size={18} weight="duotone" /> },
  warn:      { bg: "bg-orange-50 border-orange-200 text-orange-900", icon: <Warning size={18} weight="duotone" /> },
  motivate:  { bg: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: <Flag size={18} weight="duotone" /> },
  celebrate: { bg: "bg-amber-50 border-amber-200 text-amber-900", icon: <Sparkle size={18} weight="duotone" /> },
};

export default function TeleproAlertsBanner({ onCta }) {
  const [alerts, setAlerts] = useState([]);
  const [tick, setTick] = useState(0); // manual re-render after dismiss

  const fetchAlerts = useCallback(async () => {
    try {
      const r = await teleproApi.get("/me/alerts");
      setAlerts(Array.isArray(r?.data?.alerts) ? r.data.alerts : []);
    } catch (_e) {
      // Silent — this is a nice-to-have banner, never block the portal.
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 60_000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  const visible = alerts.filter((a) => !isDismissedToday(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="telepro-alerts-banner">
      {visible.map((a) => {
        const style = LEVEL_STYLES[a.level] || LEVEL_STYLES.info;
        return (
          <div
            key={a.id}
            className={`flex items-start gap-3 border rounded-sm px-4 py-3 ${style.bg}`}
            data-testid={`alert-${a.id}`}
          >
            <div className="shrink-0 mt-0.5">{style.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{a.title}</div>
              <div className="text-xs opacity-90 mt-0.5">{a.message}</div>
              {a.cta?.label && (
                <button
                  onClick={() => onCta?.(a)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline decoration-2 underline-offset-2"
                  data-testid={`alert-${a.id}-cta`}
                >
                  {a.cta.label} <ArrowRight size={12} weight="bold" />
                </button>
              )}
            </div>
            <button
              onClick={async () => {
                markDismissedToday(a.id);
                // Broadcasts: persist dismissal on the server so it doesn't come back on another device.
                if (a.id?.startsWith("broadcast:")) {
                  try { await teleproApi.post(`/me/alerts/${encodeURIComponent(a.id)}/dismiss`); } catch (_e) { /* noop */ }
                }
                setTick(t => t + 1);
              }}
              className="p-1 text-current opacity-50 hover:opacity-100"
              aria-label="Fermer l'alerte"
              data-testid={`alert-${a.id}-dismiss`}
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        );
      })}
      {/* Trick to force re-eval after dismiss without extra fetch */}
      <span className="hidden" data-tick={tick} />
    </div>
  );
}
